import { Button } from '@/components/commons';
import { VideoUploadField } from '@/components/molecules/VideoUploadField';
import { AyumiService } from '@/services/ayumi';
import { IResponseError } from '@/types/common';
import { ISaveAyumiIntroVideoItemRequest } from '@/types/request';
import { IAyumiIntroVideoItem } from '@/types/response';
import { NOTIFY_STATUS, notificationMessage, notificationModal } from '@/utils';
import { AYUMI_VIDEO_PROFILE } from '@/utils/videoValidation';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

type FormValues = {
  videoUrl: string;
  videoPosterUrl: string;
  videoDurationSec: number | null;
  videoWidth: number | null;
  videoHeight: number | null;
};

const toFormValues = (item?: IAyumiIntroVideoItem): FormValues => ({
  videoUrl: item?.videoUrl ?? '',
  videoPosterUrl: item?.videoPosterUrl ?? '',
  videoDurationSec:
    item?.videoDurationSec != null ? Number(item.videoDurationSec) : null,
  videoWidth: item?.videoWidth ?? null,
  videoHeight: item?.videoHeight ?? null,
});

interface IProps {
  /** 未指定なら「動画を追加」で出した未保存のカード */
  item?: IAyumiIntroVideoItem;
  /** 表示順（1 始まり）。アプリのカルーセルでは 1 本目が最初に真ん中に来る */
  position: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (direction: -1 | 1) => void;
  onSaved: () => void;
  onCancelNew?: () => void;
}

/**
 * 課題一覧の紹介動画 1 本ぶん。カテゴリカードと同じく 1 枚ずつ独立して保存する。
 * 動画を外したいときは差し替えではなく削除する（空の動画は保存できない）。
 */
export const IntroVideoItemCard: React.FC<IProps> = ({
  item,
  position,
  canMoveUp,
  canMoveDown,
  onMove,
  onSaved,
  onCancelNew,
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: toFormValues(item),
  });
  const { handleSubmit, reset, formState } = methods;
  const { isDirty } = formState;

  const { mutate: save, isLoading } = useMutation(
    (body: ISaveAyumiIntroVideoItemRequest) =>
      item
        ? AyumiService.updateIntroVideoItem(body, item.id)
        : AyumiService.createIntroVideoItem(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: '紹介動画を保存しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        onSaved();
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: '紹介動画の保存に失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
        console.error(error);
      },
    },
  );

  const { mutate: remove, isLoading: isDeleting } = useMutation(
    () => AyumiService.deleteIntroVideoItem(item!.id),
    {
      onSuccess: () => {
        notificationMessage({
          message: '紹介動画を削除しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        onSaved();
      },
      onError: () => {
        notificationMessage({
          message: '紹介動画の削除に失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const onSubmit = (values: FormValues) => {
    if (!values.videoUrl) {
      notificationMessage({
        message: '動画を選択してください。',
        type: NOTIFY_STATUS.ERROR,
      });

      return;
    }

    save(
      {
        videoUrl: values.videoUrl,
        videoPosterUrl: values.videoPosterUrl || null,
        videoDurationSec: values.videoDurationSec ?? null,
        videoWidth: values.videoWidth ?? null,
        videoHeight: values.videoHeight ?? null,
      },
      // 既存カードは保存しても再マウントしないため、送った値を新しい既定値にして
      // 「未保存」表示を落とす
      { onSuccess: () => reset(values) },
    );
  };

  return (
    <FormProvider {...methods}>
      <div className='flex flex-col gap-3 p-4 rounded border bg-[#f7f7f7]'>
        <div className='flex items-center justify-end gap-2'>
          <span className='mr-auto text-[14px] font-bold'>
            {item ? `${position} 本目` : '新しい紹介動画'}
            {isDirty && (
              <span className='ml-3 text-[12px] text-[#d46b08]'>
                未保存の変更があります
              </span>
            )}
          </span>
          <Button
            icon={<ArrowUpOutlined />}
            disabled={!canMoveUp}
            onClick={() => onMove(-1)}
          />
          <Button
            icon={<ArrowDownOutlined />}
            disabled={!canMoveDown}
            onClick={() => onMove(1)}
          />
          <Button
            type='primary'
            icon={<SaveOutlined />}
            loading={isLoading}
            disabled={isUploading}
            onClick={handleSubmit(onSubmit)}
          >
            この動画を保存
          </Button>
          {item ? (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={isDeleting}
              onClick={() =>
                notificationModal({
                  type: NOTIFY_STATUS.CONFIRM,
                  title: '確認',
                  content:
                    'この紹介動画を削除します。アプリのカルーセルからも消えます。よろしいですか？',
                  maskClosable: false,
                  onOk: () => remove(),
                })
              }
            >
              削除
            </Button>
          ) : (
            <Button onClick={onCancelNew}>キャンセル</Button>
          )}
        </div>

        <VideoUploadField
          name='videoUrl'
          posterName='videoPosterUrl'
          durationName='videoDurationSec'
          widthName='videoWidth'
          heightName='videoHeight'
          label='動画'
          profile={AYUMI_VIDEO_PROFILE}
          allowUrlInput={false}
          onUploadingChange={setIsUploading}
        />
      </div>
    </FormProvider>
  );
};
